export const MENTION_ALL_ID = "all";
export const MENTION_ALL_TOKEN = "@all";
export const MENTION_USER_TOKEN_PREFIX = "@user:";
export const MENTION_USER_TOKEN_REGEX = /@user:([A-Za-z0-9_-]+)\b/g;

const LEGACY_ALL_LABELS = ["Todos", "All"];

export const getOwnerDisplayLabel = ({ owner }) => {
  if (!owner) return "";
  return owner?.name?.trim() || owner?.email || "";
};

export const getMentionAllLabel = ({ t, allLabelKey }) => t(allLabelKey);

export const toMentionToken = ({ id, type = "user" }) => {
  if (type === "all" || id === MENTION_ALL_ID) {
    return MENTION_ALL_TOKEN;
  }

  return `${MENTION_USER_TOKEN_PREFIX}${id}`;
};

export const parseMentionTokenAt = ({ text, atIndex }) => {
  if (!text || atIndex < 0 || text[atIndex] !== "@") {
    return null;
  }

  if (text.startsWith(MENTION_ALL_TOKEN, atIndex)) {
    const end = atIndex + MENTION_ALL_TOKEN.length;
    const after = text[end];

    if (after === undefined || after === " " || after === "\n") {
      return { type: "all", token: MENTION_ALL_TOKEN, start: atIndex, end };
    }
  }

  const userMatch = text.slice(atIndex).match(/^@user:([A-Za-z0-9_-]+)\b/);

  if (userMatch) {
    const token = userMatch[0];
    const end = atIndex + token.length;
    const after = text[end];

    if (after === undefined || after === " " || after === "\n") {
      return {
        type: "user",
        userID: userMatch[1],
        token,
        start: atIndex,
        end,
      };
    }
  }

  return null;
};

const getLegacyMentionableLabels = ({ ownersByID, t, allLabelKey }) => {
  const labels = Object.values(ownersByID)
    .map((owner) => getOwnerDisplayLabel({ owner }))
    .filter(Boolean);

  labels.push(getMentionAllLabel({ t, allLabelKey }));
  labels.push(...LEGACY_ALL_LABELS);

  return [...new Set(labels)].sort((left, right) => right.length - left.length);
};

export const getMentionDisplayLabel = ({
  mention,
  ownersByID,
  t,
  allLabelKey,
}) => {
  if (mention.type === "all") {
    return getMentionAllLabel({ t, allLabelKey });
  }

  if (mention.type === "user" && mention.userID) {
    return getOwnerDisplayLabel({ owner: ownersByID[mention.userID] });
  }

  return mention.label || "";
};

export const buildMentionOptions = ({
  ownersByID = {},
  query = "",
  t,
  allLabelKey,
  excludeUserID = null,
}) => {
  const normalizedQuery = query.trim().toLowerCase();
  const allLabel = getMentionAllLabel({ t, allLabelKey });

  const allOption = {
    id: MENTION_ALL_ID,
    label: allLabel,
    type: "all",
  };

  const ownerOptions = Object.entries(ownersByID)
    .filter(([userID]) => userID !== excludeUserID)
    .map(([userID, owner]) => ({
      id: userID,
      label: getOwnerDisplayLabel({ owner }),
      type: "user",
    }))
    .filter((option) => option.label)
    .filter((option) => {
      if (!normalizedQuery) return true;
      return option.label.toLowerCase().includes(normalizedQuery);
    })
    .sort((left, right) => left.label.localeCompare(right.label));

  const showAll =
    !normalizedQuery || allLabel.toLowerCase().includes(normalizedQuery);

  return [...(showAll ? [allOption] : []), ...ownerOptions];
};

const extractTokenMentionsFromText = ({ text }) => {
  if (!text) return [];

  const mentions = [];
  let index = 0;

  while (index < text.length) {
    const atIndex = text.indexOf("@", index);
    if (atIndex === -1) break;

    const parsed = parseMentionTokenAt({ text, atIndex });

    if (parsed) {
      mentions.push(parsed);
      index = parsed.end;
    } else {
      index = atIndex + 1;
    }
  }

  return mentions;
};

const extractLegacyMentionsFromText = ({
  text,
  ownersByID,
  t,
  allLabelKey,
  occupiedRanges = [],
}) => {
  if (!text) return [];

  const labels = getLegacyMentionableLabels({ ownersByID, t, allLabelKey });
  const mentions = [];
  let index = 0;

  const overlapsOccupied = (start, end) =>
    occupiedRanges.some((range) => start < range.end && end > range.start);

  while (index < text.length) {
    const atIndex = text.indexOf("@", index);
    if (atIndex === -1) break;

    let matched = null;

    for (const label of labels) {
      const candidate = `@${label}`;
      if (!text.startsWith(candidate, atIndex)) continue;

      const end = atIndex + candidate.length;
      if (overlapsOccupied(atIndex, end)) continue;

      const afterMention = text[end];
      if (
        afterMention === undefined ||
        afterMention === " " ||
        afterMention === "\n"
      ) {
        matched = {
          type: "legacy",
          label,
          token: candidate,
          start: atIndex,
          end,
        };
        break;
      }
    }

    if (matched) {
      mentions.push(matched);
      index = matched.end;
    } else {
      index = atIndex + 1;
    }
  }

  return mentions;
};

export const extractMentionsFromText = ({
  text,
  ownersByID,
  t,
  allLabelKey,
}) => {
  const tokenMentions = extractTokenMentionsFromText({ text });
  const occupiedRanges = tokenMentions.map((mention) => ({
    start: mention.start,
    end: mention.end,
  }));
  const legacyMentions = extractLegacyMentionsFromText({
    text,
    ownersByID,
    t,
    allLabelKey,
    occupiedRanges,
  });

  return [...tokenMentions, ...legacyMentions].sort(
    (left, right) => left.start - right.start
  );
};

export const resolveMentionedUserIDs = ({
  text,
  ownersByID,
  t,
  allLabelKey,
  excludeUserID = null,
}) => {
  const mentions = extractMentionsFromText({
    text,
    ownersByID,
    t,
    allLabelKey,
  });

  const hasAll = mentions.some((mention) => {
    if (mention.type === "all") return true;

    if (mention.type !== "legacy") return false;

    return (
      LEGACY_ALL_LABELS.includes(mention.label) ||
      mention.label === getMentionAllLabel({ t, allLabelKey })
    );
  });

  if (hasAll) return [MENTION_ALL_ID];

  const userIDs = [];
  const seen = new Set();

  mentions.forEach((mention) => {
    if (mention.type === "user" && mention.userID) {
      if (!seen.has(mention.userID)) {
        seen.add(mention.userID);
        userIDs.push(mention.userID);
      }
      return;
    }

    if (mention.type === "legacy" && mention.label) {
      const entry = Object.entries(ownersByID).find(
        ([, owner]) => getOwnerDisplayLabel({ owner }) === mention.label
      );

      if (entry && !seen.has(entry[0])) {
        seen.add(entry[0]);
        userIDs.push(entry[0]);
      }
    }
  });

  if (!excludeUserID) return userIDs;

  return userIDs.filter((userID) => userID !== excludeUserID);
};

export const getMentionQueryState = ({ text, cursorPosition }) => {
  const beforeCursor = text.slice(0, cursorPosition);
  const atIndex = beforeCursor.lastIndexOf("@");
  if (atIndex === -1) return null;

  const between = beforeCursor.slice(atIndex + 1);
  if (/\s/.test(between)) return null;

  return {
    query: between,
    queryStart: atIndex,
  };
};

export const insertMention = ({ text, queryStart, queryLength, option }) => {
  const before = text.slice(0, queryStart);
  const after = text.slice(queryStart + 1 + queryLength);
  const mentionText = `@${option.label} `;
  const newText = `${before}${mentionText}${after}`;
  const newCursorPosition = before.length + mentionText.length;

  return { text: newText, cursorPosition: newCursorPosition };
};

export const removeMentionAtCursor = ({
  text,
  cursorPosition,
  ownersByID,
  t,
  allLabelKey,
  direction = "backward",
}) => {
  const mentions = extractMentionsFromText({
    text,
    ownersByID,
    t,
    allLabelKey,
  });

  const mention =
    direction === "forward"
      ? mentions.find((item) => item.start === cursorPosition)
      : mentions.find((item) => item.end === cursorPosition);

  if (!mention) return null;

  const newText = text.slice(0, mention.start) + text.slice(mention.end);

  return { text: newText, cursorPosition: mention.start };
};

export const renderAnnotationTextSegments = ({
  text,
  ownersByID,
  t,
  allLabelKey,
}) => {
  const mentions = extractMentionsFromText({
    text,
    ownersByID,
    t,
    allLabelKey,
  });

  if (mentions.length === 0) {
    return [{ type: "text", value: text }];
  }

  const segments = [];
  let lastIndex = 0;

  mentions.forEach((mention) => {
    if (mention.start > lastIndex) {
      segments.push({
        type: "text",
        value: text.slice(lastIndex, mention.start),
      });
    }

    const displayLabel = getMentionDisplayLabel({
      mention,
      ownersByID,
      t,
      allLabelKey,
    });

    segments.push({
      type: "mention",
      value: displayLabel
        ? `@${displayLabel}`
        : text.slice(mention.start, mention.end),
    });
    lastIndex = mention.end;
  });

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments;
};

export const normalizeAnnotationTextForEdit = ({
  text,
  ownersByID,
  t,
  allLabelKey,
}) => {
  if (!text) return "";

  const mentions = extractMentionsFromText({
    text,
    ownersByID,
    t,
    allLabelKey,
  });

  if (mentions.length === 0) return text;

  let result = "";
  let lastIndex = 0;

  mentions.forEach((mention) => {
    if (mention.start > lastIndex) {
      result += text.slice(lastIndex, mention.start);
    }

    if (mention.type === "user" || mention.type === "all") {
      const displayLabel = getMentionDisplayLabel({
        mention,
        ownersByID,
        t,
        allLabelKey,
      });
      result += displayLabel
        ? `@${displayLabel}`
        : text.slice(mention.start, mention.end);
    } else {
      result += text.slice(mention.start, mention.end);
    }

    lastIndex = mention.end;
  });

  result += text.slice(lastIndex);
  return result;
};
